import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { Link as RouterLink } from 'react-router-dom';
import connectionsService from '../../services/connectionsService';
import { deliveryService } from '../../services/deliveryService';
import { openOAuthPopup } from '../../utils/oauthPopup';

const CONNECT_POLL_MS = 3000;
const CONNECT_POLL_TIMEOUT_MS = 120000;

const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events';
const CALENDAR_CONNECTOR = 'GOOGLE_CALENDAR';

const SCOPE_LABELS = {
  'https://www.googleapis.com/auth/drive.file': 'Google Sheets',
  [CALENDAR_SCOPE]: 'Google Calendar',
};

const PROVIDER_META = {
  google: { label: 'Google', icon: <GoogleIcon fontSize="small" /> },
};

const scopeLabel = (scope) => SCOPE_LABELS[scope] || scope;

/**
 * Settings → Connections: every account this environment has authorised with a provider, and
 * disconnecting one. Google is the only provider today; Slack and Teams join this list once
 * their connectors ship, with no change to how a row renders.
 */
const ConnectionsSettings = () => {
  // Google is only used by Sheets and Calendar, both Team; connecting it on Free granted access
  // nothing could use
  const { features } = useEntitlements();
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const envId = selectedEnvironment?.id;

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [connecting, setConnecting] = useState(null); // 'sheets' | 'calendar' | null
  const pollRef = useRef(null);

  // Whether this deployment ships the Google Calendar connector at all. Asking `/api/connectors`
  // rather than carrying a second flag in the UI keeps one answer to "can we do calendar?": the
  // connector is a conditional bean (GoogleCalendarConnector), so when it is off the destination
  // picker, environment defaults and this button all go quiet together. Offering "Add calendar
  // access" without it would send a merchant through Google's unverified-app consent screen to
  // grant a scope nothing can then use.
  const [calendarOffered, setCalendarOffered] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null); // { id, accountLabel } | null
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current.interval);
      clearTimeout(pollRef.current.timeout);
      pollRef.current = null;
    }
    setConnecting(null);
  }, []);

  const load = useCallback(async () => {
    if (!envId) return [];
    setError(null);
    try {
      const list = await connectionsService.list(envId, authenticatedFetch);
      setConnections(list || []);
      return list || [];
    } catch (e) {
      setError(e.message || 'Failed to load connections');
      return [];
    }
    // authenticatedFetch is recreated every render; a dependency here would poll in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  useEffect(() => {
    if (!envId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    load().finally(() => setLoading(false));
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  useEffect(() => {
    let cancelled = false;
    deliveryService.listConnectors(authenticatedFetch)
      .then((list) => {
        if (!cancelled) {
          setCalendarOffered((list || []).some((c) => c.type === CALENDAR_CONNECTOR));
        }
      })
      // A connector list we could not read is not a reason to offer calendar on a hunch, and not
      // a reason to shout at someone who came here to connect a spreadsheet either.
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleConnection = connections.find((c) => c.provider === 'google');

  const handleConnect = (scope) => {
    setError(null);
    // The popup tells us the moment Google answers; the poll below stays as the fallback for a
    // popup closed by hand or a message that never arrives.
    openOAuthPopup(connectionsService.connectUrl('google', envId, scope), ({ result }) => {
      if (result === 'connected') {
        load().finally(stopPolling);
        return;
      }
      setError(result === 'denied'
        ? 'Google access was not granted, so nothing is connected yet.'
        : 'Connecting your Google account failed. Try again.');
      stopPolling();
    });
    setConnecting(scope);
    const startedAt = Date.now();
    const interval = setInterval(async () => {
      const list = await load();
      const google = list.find((c) => c.provider === 'google');
      const satisfied = scope === 'calendar' ? google?.scopes?.includes(CALENDAR_SCOPE) : !!google;
      if (satisfied || Date.now() - startedAt > CONNECT_POLL_TIMEOUT_MS) {
        stopPolling();
      }
    }, CONNECT_POLL_MS);
    const timeout = setTimeout(stopPolling, CONNECT_POLL_TIMEOUT_MS);
    pollRef.current = { interval, timeout };
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await connectionsService.delete(envId, deleteTarget.id, authenticatedFetch);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setDeleteError(e.message || 'Failed to disconnect');
    } finally {
      setDeleting(false);
    }
  };

  if (!envId) {
    return <Alert severity="warning">Please select an environment first</Alert>;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Connections
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Accounts this environment has authorised, reused by any destination that needs one.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }} data-testid="connections-error">
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {connections.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No connections yet.
              </Typography>
            )}

            {connections.map((connection) => {
              const meta = PROVIDER_META[connection.provider] || {
                label: connection.provider,
                icon: null,
              };
              const hasCalendar = connection.scopes?.includes(CALENDAR_SCOPE);
              return (
                <Box
                  key={connection.id}
                  data-testid={`connection-row-${connection.id}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {meta.icon}
                    <Box>
                      <Typography variant="body1">
                        {meta.label}
                        {connection.accountLabel ? ` (${connection.accountLabel})` : ''}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                        {(connection.scopes || []).map((scope) => (
                          <Chip key={scope} size="small" label={scopeLabel(scope)} />
                        ))}
                        <Chip
                          size="small"
                          label={connection.status === 'ACTIVE' ? 'Connected' : connection.status}
                          color={connection.status === 'ACTIVE' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    {calendarOffered && connection.provider === 'google' && !hasCalendar && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EventAvailableIcon />}
                        onClick={() => handleConnect('calendar')}
                        disabled={connecting !== null}
                        data-testid="add-calendar-access-button"
                      >
                        {connecting === 'calendar' ? 'Waiting for Google…' : 'Add calendar access'}
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget({ id: connection.id, accountLabel: connection.accountLabel })}
                      data-testid={`disconnect-button-${connection.id}`}
                    >
                      Disconnect
                    </Button>
                  </Stack>
                </Box>
              );
            })}

            {!googleConnection && (
              <>
                <Divider />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {features?.deliveries
                      ? 'Connect a Google account to deliver answers into a spreadsheet.'
                      : 'With Team, answers go straight into a Google spreadsheet or your calendar.'}
                  </Typography>
                  {!features?.deliveries ? (
                    <Button variant="contained" component={RouterLink} to="/settings?tab=billing">
                      See Team
                    </Button>
                  ) : (
                  <Button
                    variant="contained"
                    startIcon={<GoogleIcon />}
                    onClick={() => handleConnect('sheets')}
                    disabled={connecting !== null}
                    data-testid="connect-google-button"
                  >
                    {connecting === 'sheets' ? 'Waiting for Google…' : 'Connect Google'}
                  </Button>
                  )}
                  {connecting === 'sheets' && <CircularProgress size={16} sx={{ ml: 1.5 }} />}
                </Box>
              </>
            )}
          </Stack>
        )}
      </CardContent>

      <Dialog open={!!deleteTarget} onClose={() => (deleting ? null : setDeleteTarget(null))}>
        <DialogTitle>Disconnect this account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Destinations using {deleteTarget?.accountLabel || 'this connection'} will keep working
            until their next delivery, which will then fail until you reconnect.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting} data-testid="confirm-disconnect-button">
            {deleting ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ConnectionsSettings;
